import { z } from 'zod';
import { ICompanyFormationFullObject } from './ICompanyFormation';
import { IDocument } from './IDocument';
import { IDocumentDirectory } from './IDocumentDirectory';
import { IDocumentDirectoryHasDocument } from './IDocumentDirectoryHasDocument';
import { IDraftMessageFullObject } from './IDraftMessage';
import { IInheritFullObject } from './IInherit';
import { ILegalClient } from './ILegalClient';
import { ILegalDependencyData } from './ILegalDependency';
import { ILegalPhaseFullObject } from './ILegalPhase';
import { ILegalRole } from './ILegalRole';
import { ILegalRole_canInvite } from './ILegalRole_canInvite';
import { ILegalTransaction_has_LegalTransactionSpecialFunction } from './ILegalTransaction_has_LegalTransactionSpecialFunction';
import type { ILegalTransactionCustomId } from './ILegalTransactionCustomId';
import { ILegalTransactionGwGFullObject } from './ILegalTransactionGwG';
import { ILegalTransactionLog } from './ILegalTransactionLog';
import type { IMeasure } from './ILegalTransactionMeasure';
import { ILegalTransactionMeeting } from './ILegalTransactionMeeting';
import { ILegalTransactionMeetingEmail } from './ILegalTransactionMeetingEmail';
import { ILegalTransactionRecordNumber } from './ILegalTransactionRecordNumber';
import { ILegalTransactionRelationship } from './ILegalTransactionRelationship/ILegalTransactionRelationship';
import { ILegalTransactionTransactionNumber } from './ILegalTransactionTransactionNumber';
import { IMortgageFullObject } from './IMortgage';
import { ILegaltransactionNote } from './ILegaltransactionNote';
import { IInvoiceFullObject } from './IInvoice';
import { IProcessManager } from './IProcessManager';
import { ITaskFullObject } from './ITask';
import { ILegalTransactionTalkRoomDraft } from './ILegalTransactionTalkRoom';

export const legalTransactionSchema = z.object({
  Created_at: z.string(),
  Closed_at: z.string().nullable(),
  idLegalTransaction: z.number(),
  idLegalTransactionState: z.number(),
  idLegalTransactionType: z.number(),
  idLegalTransactionProgress: z.number(),
  Title: z.string().nullable(),
  Notary_idUser: z.number(),
  Remarks: z.string().nullable(),
  Employee_idUser: z.number().nullable(),
  LastPhaseUpdate_at: z.string().nullable(),
  migrateType: z.boolean().optional(),
  LegalTransactionState: z.string().optional(),
  LegalTransactionType: z.string().optional(),
  LegalTransactionProgress: z.string().optional(),
  FirstName: z.string().optional(),
  LastName: z.string().optional(),
  idUserGroup: z.number().nullable(),
  UserGroup: z.string().nullable().optional(),
  NotarizationDate: z.string().nullable(),
  StartEditDate: z.string().nullable(),
  // UserInformed_LackOfActivity: z.boolean().optional(),
  UserInformed_LackOfActivity: z
    .union([z.boolean(), z.number().transform(val => val === 1)])
    .optional(),
  LastActivity_at: z.string().nullable(),
  // ARNOtop Sync
  syncWithArnoTop: z
    .union([z.boolean(), z.number()])
    .transform(val => Boolean(val)),
  syncAllLegalClientsWithArnotop: z.boolean().optional(),
  idArnoTopObject: z.number().nullable(),
  // Gemeinde/Gemarkung – Default für alle Phasen, kann je Phase überschrieben werden.
  idAdministrativeUnit: z.number().nullable().optional(),
  AdministrativeUnit: z.string().nullable().optional(),
  // Flag „Mandant soll Termin vereinbaren" (Call-to-Action im Mandantenportal).
  RequestClientMeeting: z
    .union([z.boolean(), z.number().transform(val => val === 1)])
    .optional(),
  // Flag „Mandant soll Ausweis + Stammdaten erfassen" (Portal-CTA).
  RequestClientData: z
    .union([z.boolean(), z.number().transform(val => val === 1)])
    .optional(),
  // Transient (nur beim Save): ausgewählte Beteiligte der Datenerfassungs-
  // Aufforderung.
  RequestDataMailClientIds: z.array(z.number()).optional(),
  // Transient (nur beim Save): pro-Mandant-Konfiguration der Terminaufforderung
  // (wer aufgefordert wird + optionale Terminart/Standort/Notiz je Mandant).
  RequestMeetingClientConfig: z
    .array(
      z.object({
        idLegalClient: z.number(),
        idMeetingType: z.number().nullable(),
        idLocation: z.number().nullable(),
        note: z.string(),
      })
    )
    .optional(),
});

export type ILegalTransaction = z.infer<typeof legalTransactionSchema>;

export type ILegalTransactionDataCollection =
  | ICompanyFormationFullObject
  | IInheritFullObject
  | IMortgageFullObject;

export interface ILegalTransactionFullObject extends ILegalTransaction {
  LogArray?: ILegalTransactionLog[];
  DataCollection: ILegalTransactionDataCollection[];
  RelationshipArray: ILegalTransactionRelationship[];
  LegalPhaseArray: ILegalPhaseFullObject[];
  LegalTransactionGwGArray: ILegalTransactionGwGFullObject[];
  CertificateArray: IDocument[];
  InvoiceArray: IDocument[];
  InvoiceObjectArray: IInvoiceFullObject[];
  DraftArray: IDocument[];
  DraftMessageArray: IDraftMessageFullObject[];
  NoteArray: ILegaltransactionNote[];
  LegalRoleArray: ILegalRole[];
  LegalClientArray: ILegalClient[];
  LegalRoleCanInviteArray: ILegalRole_canInvite[];
  ProcessManagerArray: IProcessManager[];
  Tasks: ITaskFullObject[];
  // Für Datenablage -> nur Intern
  DocumentArray: IDocumentDirectoryHasDocument[];
  DocumentDirectoryArray: IDocumentDirectory[];
  // Termine
  LegalTransactionMeetingArray: ILegalTransactionMeeting[];
  LegalTransactionMeetingEmailArray: ILegalTransactionMeetingEmail[];
  TransactionNumberArray: ILegalTransactionTransactionNumber[];
  RecordNumberArray: ILegalTransactionRecordNumber[];
  LegalTransaction_has_LegalTransactionSpecialFunctionArray: ILegalTransaction_has_LegalTransactionSpecialFunction[];
  customIds?: ILegalTransactionCustomId[];
  Measurements?: IMeasure[];
  //NextCloud
  LegalTransactionTalkRoomArray?: ILegalTransactionTalkRoomDraft[];
  //Sonstige bzw. weitere Dokumente
  OtherDocumentArray: IDocument[];
  legalDependencyData?: ILegalDependencyData;
}
